import React, { useEffect, useRef, useState } from "react";
import { Card, Icon, Tooltip } from "@mui/material";
import MDTypography from "components/MDTypography";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import "./Dashboard.css"; // Custom CSS for resizing
import MDBox from "components/MDBox";
import useAxiosPrivate from "hooks/useAxiosPrivate";
import useBackendError from "hooks/useBackendError";
import useAuth from "hooks/useAuth";
import MDButton from "components/MDButton";
import colors from "assets/theme/base/colors";
import { makeStyles } from "@material-ui/styles";
import DashboardWidgetModal from "components/modal/DashboardWidgetModal";
import useModal from "hooks/useModal";
import { dashCards } from "utils/utils";
import SatisfactionOverview from "./dashcards/SatisfactionOverview";
import Top5Citations from "./dashcards/Top5Citations";

const useStyles = makeStyles({
  button_delete: {
    "&:hover": {
      color: colors.error.main,
    },
  },
});

const Dashboard = React.memo(() => {
  const classes = useStyles();
  const [isEditing, setEditing] = useState(false);
  const axios = useAxiosPrivate();
  const { setError } = useBackendError();
  const { auth, setAuth } = useAuth();
  const { openModal } = useModal();
  const [newWidgetId, setNewWidgetId] = useState(null);

  const initialLayout = React.useMemo(
    () =>
      auth.profile.dashboardConfig.map((item) => ({
        ...item,
        resizeHandles: ["s", "e", "n", "w", "se", "sw", "ne", "nw"],
      })),
    [auth.profile.dashboardConfig]
  );

  const [layout, setLayout] = useState(initialLayout);

  const ref = useRef();

  const onLayoutChange = (newLayout) => {
    if (JSON.stringify(layout) !== JSON.stringify(newLayout)) {
      setLayout(newLayout);
    }
  };

  const edit = () => {
    setEditing(true);
  };

  const save = async () => {
    setEditing(false);
    try {
      await axios.post(`/dashboard`, layout, {
        headers: { "Content-Type": "application/json" },
      });
    } catch (err) {
      setError("Failed to save to the backend. Please try again");
    }
    setAuth((prevAuth) => ({
      ...prevAuth,
      profile: {
        ...prevAuth.profile,
        dashboardConfig: layout,
      },
    }));
  };

  const deleteMe = (index, event) => {
    event.stopPropagation();
    setLayout((prevLayout) => {
      // Remove the item at the specified index
      const newLayout = prevLayout.filter((_, i) => i !== index);
      return newLayout;
    });
  };

  const modalReturnedValue = (result) => {
    if (!result.action === "add") {
      return;
    }
    const newVal = dashCards.filter((val) => result.widget === val.id)[0];
    setLayout((prevLayout) =>
      prevLayout.concat({
        w: newVal.w,
        h: newVal.h,
        x: 0,
        y: 0,
        i: newVal.id,
        resizeHandles: ["s", "e", "n", "w", "se", "sw", "ne", "nw"],
      })
    );
    setNewWidgetId(result.widget);
  };

  useEffect(() => {
    if (newWidgetId) {
      const gridLayout = document.querySelector(".react-grid-layout");
      const gridItem = document.querySelector(`[data-grid-id="${newWidgetId}"]`);

      if (gridLayout && gridItem) {
        const gridRect = gridLayout.getBoundingClientRect(); // Grid container dimensions
        const gridWidth = gridRect.width;
        const colWidth = gridWidth / 12; // Assuming 12 columns in the grid
        const rowHeight = 30; // Row height defined in GridLayout

        const handleMouseMove = (event) => {
          const mouseX = event.clientX - gridRect.left; // Pointer position within grid
          const mouseY = event.clientY - gridRect.top;

          const newItem = dashCards.filter((val) => val.id === newWidgetId)[0];
          // Convert to grid coordinates
          const gridX = Math.max(0, Math.floor(mouseX / colWidth - newItem.w / 2));
          const gridY = Math.max(0, Math.floor(mouseY / rowHeight - newItem.h / 2));

          // Update layout
          setLayout((prevLayout) =>
            prevLayout.map((item) =>
              item.i === newWidgetId
                ? { ...item, x: gridX, y: gridY } // Center under pointer
                : item
            )
          );

          // Simulate mousedown
          const mouseDownEvent = new MouseEvent("mousedown", {
            bubbles: true,
            cancelable: true,
            clientX: event.clientX,
            clientY: event.clientY,
          });

          gridItem.dispatchEvent(mouseDownEvent);

          window.removeEventListener("mousemove", handleMouseMove);
        };

        window.addEventListener("mousemove", handleMouseMove);

        // Automatically trigger the mouse move at current pointer position
        document.body.dispatchEvent(
          new MouseEvent("mousemove", {
            clientX: window.innerWidth / 2, // Example initial position
            clientY: window.innerHeight / 2,
          })
        );
      }

      setNewWidgetId(null); // Reset the widget ID after processing
    }
  }, [newWidgetId]);

  const addNew = () => {
    openModal(<DashboardWidgetModal />, modalReturnedValue);
  };

  return (
    <MDBox mt={-2}>
      <MDBox textAlign="right" display="flex" ml={2}>
        {!isEditing && (
          <MDTypography
            variant="body2"
            color="info"
            style={{ textDecoration: "underline", fontSize: 12, cursor: "pointer" }}
            onClick={edit}
          >
            <Icon>edit</Icon> Edit Dashboard
          </MDTypography>
        )}
        {isEditing && (
          <>
            <MDTypography
              variant="body2"
              color="primary"
              style={{ textDecoration: "underline", fontSize: 12, cursor: "pointer" }}
              onClick={save}
            >
              <Icon>save</Icon> Save Dashboard
            </MDTypography>
            <MDTypography
              variant="body2"
              color="info"
              style={{
                textDecoration: "underline",
                fontSize: 12,
                cursor: "pointer",
                marginLeft: 10,
              }}
              onClick={addNew}
            >
              <Icon>add</Icon> Add New Widget
            </MDTypography>
          </>
        )}
      </MDBox>

      <GridLayout
        ref={ref}
        className={isEditing ? "layout" : null}
        layout={layout}
        cols={24}
        rowHeight={30}
        width={1200}
        draggableHandle=".resizable-item"
        isDraggable={isEditing}
        isResizable={isEditing}
        onLayoutChange={onLayoutChange}
        draggableCancel=".cancelSelectorName"
      >
        {layout.map((card, index) => (
          <Card
            key={card.i}
            data-grid-id={card.i}
            className={isEditing ? "resizable-item" : null}
            sx={{ paddingTop: 1, paddingBottom: 1, paddingLeft: 2, paddingRight: 2 }}
          >
            {isEditing && (
              <MDButton
                variant="text"
                color="secondary"
                iconOnly
                circular
                sx={{ position: "absolute", top: 4, right: 4 }}
                className={`cancelSelectorName ${classes.button_delete}`}
                onClick={(event) => deleteMe(index, event)}
                onMouseDown={(event) => {
                  event.stopPropagation;
                }}
                onTouchStart={(event) => {
                  event.stopPropagation;
                }}
              >
                <Tooltip title="remove this widget" enterDelay={500}>
                  <Icon>delete</Icon>
                </Tooltip>
              </MDButton>
            )}
            {card.i === "SatisfactionOverview" && <SatisfactionOverview />}
            {card.i === "Top5Citations" && <Top5Citations />}
          </Card>
        ))}
      </GridLayout>
    </MDBox>
  );
});

export default Dashboard;
